import React from "react";
import { Link } from "react-router-dom";
import logo from "./assetpal-logo.png";
import "./LegalPage.css";

function TermsOfService() {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="legal-nav-brand">
          <img src={logo} alt="AssetPal Logo" className="legal-nav-logo" />
          <span>AssetPal</span>
        </Link>
        <div className="legal-nav-links">
          <Link to="/privacy" className="legal-nav-link">Privacy Policy</Link>
          <Link to="/" className="legal-nav-cta">← Back to Home</Link>
        </div>
      </nav>

      <div className="legal-container">
        <div className="legal-header">
          <h1>Terms of Service</h1>
          <p className="legal-effective">Effective Date: April 11, 2026</p>
          <p className="legal-subtitle">Please read these terms carefully before using AssetPal.</p>
        </div>

        <div className="legal-content">

          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By downloading, installing, or using the AssetPal mobile application ("App") or visiting
              the AssetPal website ("Site"), you agree to be bound by these Terms of Service ("Terms").
              If you do not agree to these Terms, do not use our services.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you ("User" or "you") and
              AssetPal ("Company," "we," "us," or "our"). We reserve the right to update these Terms at
              any time. Continued use of the App or Site after changes are posted constitutes acceptance
              of the revised Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              AssetPal is a personal asset management application that allows users to:
            </p>
            <ul>
              <li>Track and organize home, vehicle, and personal assets</li>
              <li>Store warranties, manuals, and related documents</li>
              <li>Receive AI-generated maintenance reminders</li>
              <li>Back up and sync asset data securely to the cloud</li>
              <li>Manage asset details including purchase dates, values, and serial numbers</li>
            </ul>
            <p>
              We reserve the right to modify, suspend, or discontinue any aspect of the service at any
              time without notice.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. Eligibility</h2>
            <p>
              You must be at least 13 years of age (or the minimum age in your jurisdiction) to use
              AssetPal. By using the App, you represent and warrant that you meet this age requirement.
              If you are under 18, you confirm you have parental or guardian consent to use the service.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. User Accounts</h2>
            <p>
              To access certain features, you must create an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security of your password and account credentials</li>
              <li>Promptly notify us of any unauthorized use of your account</li>
              <li>Be responsible for all activity that occurs under your account</li>
            </ul>
            <p>
              We reserve the right to terminate accounts that violate these Terms or are inactive for
              an extended period.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Subscriptions and Payments</h2>
            <p>
              AssetPal offers both free and premium subscription tiers. Premium features are available
              through an in-app subscription ("AssetPal Pro") processed via Apple In-App Purchases.
            </p>
            <ul>
              <li>
                <strong>Billing:</strong> Subscriptions are billed on a recurring basis (monthly or
                annually) as selected at purchase.
              </li>
              <li>
                <strong>Renewal:</strong> Subscriptions automatically renew unless cancelled at least
                24 hours before the end of the current billing period.
              </li>
              <li>
                <strong>Cancellation:</strong> You may cancel your subscription at any time through your
                Apple ID account settings. Cancellation takes effect at the end of the current billing
                period.
              </li>
              <li>
                <strong>Refunds:</strong> All purchases are subject to Apple's refund policies. We do
                not offer refunds directly; please contact Apple Support for refund requests.
              </li>
              <li>
                <strong>Price Changes:</strong> We reserve the right to modify subscription pricing.
                You will be notified of any price changes before they take effect.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. User Content</h2>
            <p>
              "User Content" means any data, information, images, or documents you upload or store in
              AssetPal, including asset descriptions, photos, receipts, and warranty documents.
            </p>
            <ul>
              <li>
                <strong>Ownership:</strong> You retain full ownership of your User Content. We do not
                claim any ownership rights over your data.
              </li>
              <li>
                <strong>License to Us:</strong> By uploading content, you grant AssetPal a limited,
                non-exclusive license to store and process your content solely for the purpose of
                providing the service to you.
              </li>
              <li>
                <strong>Prohibited Content:</strong> You may not upload content that is illegal,
                infringing, harmful, or violates any applicable laws.
              </li>
              <li>
                <strong>Data Accuracy:</strong> You are solely responsible for the accuracy and
                legality of any content you submit.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>7. Prohibited Uses</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the service for any unlawful purpose or in violation of these Terms</li>
              <li>Attempt to gain unauthorized access to any part of the service or its infrastructure</li>
              <li>Reverse engineer, decompile, or disassemble any part of the App</li>
              <li>Use the service to transmit viruses, malware, or other harmful code</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Scrape, crawl, or data-mine any content from the service without permission</li>
              <li>Use the service in any way that could damage, disable, or impair our systems</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Intellectual Property</h2>
            <p>
              All intellectual property rights in the App, including but not limited to software,
              design, trademarks, logos, and content created by AssetPal, are owned by or licensed to
              us. Nothing in these Terms grants you any rights to use our intellectual property without
              our prior written consent.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Third-Party Services</h2>
            <p>
              AssetPal uses third-party services including but not limited to:
            </p>
            <ul>
              <li><strong>Supabase</strong> – for cloud database and authentication services</li>
              <li><strong>RevenueCat</strong> – for subscription management and billing</li>
              <li><strong>Apple App Store</strong> – for app distribution and in-app purchases</li>
            </ul>
            <p>
              These third-party services are governed by their own terms of service and privacy policies.
              We are not responsible for the practices of these third parties.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS
              FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the service will be uninterrupted, error-free, or free of viruses.
              You use the service at your own risk. Asset valuations and maintenance reminders are
              provided for informational purposes only and should not be relied upon for financial or
              legal decisions.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, ASSETPAL AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
              AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, LOSS OF PROFITS, OR LOSS OF
              GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE.
            </p>
            <p>
              OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS SHALL NOT EXCEED THE AMOUNT YOU PAID TO US
              IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless AssetPal and its officers, directors, employees,
              and agents from and against any claims, liabilities, damages, losses, and expenses
              (including reasonable legal fees) arising from your use of the service, your violation of
              these Terms, or your violation of any rights of a third party.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Termination</h2>
            <p>
              We may suspend or terminate your access to the service at any time, for any reason,
              with or without notice. Upon termination, your right to use the service will immediately
              cease. Sections 8, 10, 11, and 12 of these Terms shall survive termination.
            </p>
            <p>
              You may delete your account at any time through the App settings. Upon account deletion,
              your data will be removed from our systems in accordance with our Privacy Policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State
              of Tennessee, United States, without regard to its conflict of law provisions. Any
              disputes arising from these Terms shall be resolved exclusively in the courts located
              in Tennessee.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of
              material changes by updating the "Effective Date" at the top of this page and, where
              appropriate, by sending you an email notification. Your continued use of the service
              after changes are posted constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. Contact Us</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <div className="legal-contact-card">
              <p><strong>AssetPal</strong></p>
              <p>Email: <a href="mailto:support@assetpal.org">support@assetpal.org</a></p>
              <p>Website: <a href="https://assetpal.org">https://assetpal.org</a></p>
            </div>
          </section>

        </div>

        <div className="legal-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <span>·</span>
          <Link to="/">Home</Link>
        </div>
      </div>

      <footer className="legal-footer">
        <p>© {new Date().getFullYear()} AssetPal. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default TermsOfService;
